import React from "react";
import PropTypes from "prop-types";
import Clickable from "./Clickable";
import { KEYBOARD_STEP } from "./useResizableColumns";

/**
 * Bấm header lần lượt: chiều mặc định của cột → chiều ngược lại → bỏ sort
 * (về thứ tự mặc định của server: mới nhất trước). Cột số tiền/ưu tiên mặc
 * định "desc" — ai bấm Total cũng muốn thấy phiếu lớn nhất trước.
 */
export const nextSort = ({ sort, dir }, key, defaultDir = "asc") => {
  if (sort !== key) return { sort: key, dir: defaultDir };
  const current = dir || "desc";
  if (current === defaultDir) return { sort: key, dir: defaultDir === "asc" ? "desc" : "asc" };
  return { sort: "", dir: "" };
};

const ARIA_SORT = { asc: "ascending", desc: "descending" };

const SortIcon = ({ active, dir }) => {
  if (!active) {
    return <i className="bi bi-arrow-down-up tw-text-[10px] tw-opacity-0 group-hover/th:tw-opacity-60 tw-transition-opacity" aria-hidden="true" />;
  }
  return <i className={`bi ${dir === "asc" ? "bi-arrow-up" : "bi-arrow-down"} tw-text-[10px] tw-text-geist-fg`} aria-hidden="true" />;
};

SortIcon.propTypes = {
  active: PropTypes.bool,
  dir: PropTypes.string,
};

/**
 * Ô header của bảng danh sách: sort phía server (qua onSort) + tay nắm kéo giãn
 * ở mép phải. Tay nắm chặn click lan lên để kéo cột không đổi luôn thứ tự sort.
 */
const ListHeaderCell = ({
  column, className, style, sort, dir, onSort, resize,
}) => {
  const { key, label, sortKey, defaultDir, align } = column;
  const active = Boolean(sortKey) && sort === sortKey;
  const justify = align === "right" ? "tw-justify-end" : "tw-justify-start";

  const content = sortKey ? (
    <Clickable
      onClick={() => onSort(nextSort({ sort, dir }, sortKey, defaultDir))}
      title={`Sort by ${label}`}
      className={`tw-inline-flex tw-items-center tw-gap-1 tw-max-w-full ${active ? "tw-text-geist-fg" : "hover:tw-text-geist-fg"}`}
    >
      {align === "right" && <SortIcon active={active} dir={dir} />}
      <span className="tw-truncate">{label}</span>
      {align !== "right" && <SortIcon active={active} dir={dir} />}
    </Clickable>
  ) : (
    <span className="tw-truncate">{label}</span>
  );

  return (
    <th className={`${className} tw-group/th`} style={style} aria-sort={active ? ARIA_SORT[dir] || "descending" : undefined}>
      <div className={`tw-flex tw-items-center ${justify} tw-min-w-0`}>{content}</div>
      {resize && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${label} column`}
          tabIndex={0}
          title="Drag to resize · double-click to reset"
          onPointerDown={(e) => resize.start(key, e)}
          onDoubleClick={(e) => { e.stopPropagation(); resize.reset(key); }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
              e.preventDefault();
              resize.nudge(key, e.key === "ArrowLeft" ? -KEYBOARD_STEP : KEYBOARD_STEP);
            }
          }}
          className="tw-absolute tw-top-0 tw-right-0 tw-h-full tw-w-2 tw-cursor-col-resize tw-touch-none tw-flex tw-justify-center focus:tw-outline-none focus-visible:tw-bg-geist-accent/20"
        >
          <span className="tw-w-px tw-h-full tw-bg-geist-border group-hover/th:tw-bg-geist-fg-3 tw-transition-colors" />
        </div>
      )}
    </th>
  );
};

ListHeaderCell.propTypes = {
  column: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string,
    sortKey: PropTypes.string,
    defaultDir: PropTypes.oneOf(["asc", "desc"]),
    align: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  sort: PropTypes.string,
  dir: PropTypes.string,
  onSort: PropTypes.func,
  resize: PropTypes.shape({
    start: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    nudge: PropTypes.func.isRequired,
  }),
};

export default ListHeaderCell;
